import React from 'react';
import { CalendarIcon, ClockIcon, WrenchIcon, Trash2Icon } from 'lucide-react';
import { motion } from 'framer-motion';
export interface WorkLogEntry {
  date: string;
  description: string;
  hours?: number;
}
export interface TaskAttachment {
  name: string;
  url: string;
  storagePath: string;
  uploadedAt: string;
  size?: number;
}
export interface Task {
  id: string;
  taskId: string;
  title: string;
  customerId: string;
  customerName?: string;
  description: string;
  status: 'todo' | 'in-progress' | 'done' | 'planned' | 'pending';
  category: 'Ladeboks' | 'Andet';
  chargerType?: 'Zaptec 1' | 'Zaptec 2' | 'Andet';
  taskType: 'Tilbudsopgave' | 'Regningsopgave';
  agreedPlanning?: string;
  agreedInstallation?: string;
  carDeliveryDate?: string;
  hours?: number;
  materialPrice?: number;
  hourlyRate?: number;
  quotePrice?: number;
  totalPrice?: number;
  invoiced: boolean;
  subTasks?: string;
  materials?: string[];
  workLog?: WorkLogEntry[];
  attachments?: TaskAttachment[];
}
interface TaskCardProps {
  task: Task;
  index: number;
  onClick: () => void;
  onDelete: (task: Task) => void;
  readOnly?: boolean;
}
const getStatusColor = (status: string) => {
  switch (status) {
    case 'done':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'in-progress':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'planned':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'pending':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'todo':
      return 'bg-stone-100 text-stone-700 border-stone-200';
    default:
      return 'bg-amber-100 text-amber-700 border-amber-200';
  }
};
const getStatusLabel = (status: string) => {
  switch (status) {
    case 'done':
      return 'Afsluttet';
    case 'in-progress':
      return 'I gang';
    case 'planned':
      return 'Planlagt';
    case 'pending':
      return 'Afventende';
    default:
      return 'I gang';
  }
};
export function TaskCard({
  task,
  index,
  onClick,
  onDelete,
  readOnly = false
}: TaskCardProps) {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(task);
  };
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: index * 0.05
  }} className="bg-white rounded-xl p-5 shadow-sm border border-stone-100 hover:shadow-md hover:border-teal-200 transition-all cursor-pointer relative group" data-id="element-1019">
      {!readOnly && <button onClick={handleDelete} className="absolute top-3 right-3 p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100" aria-label="Slet opgave" data-id="element-1020">
          <Trash2Icon className="w-4 h-4" data-id="element-1021" />
        </button>}
      <div onClick={readOnly ? undefined : onClick} data-id="element-1022">
        <div className="flex items-start justify-between mb-3 pr-8" data-id="element-1023">
          <div className="flex-1" data-id="element-1024">
            <div className="flex items-center gap-2 mb-1" data-id="element-1025">
              <h3 className="text-lg font-semibold text-stone-900" data-id="element-1026">
                {task.title}
              </h3>
              {task.taskId && <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full border border-stone-200" data-id="element-1027">
                  {task.taskId}
                </span>}
            </div>
            {task.customerName && <p className="text-sm text-stone-500" data-id="element-1028">{task.customerName}</p>}
          </div>
          <div className="flex flex-col gap-2 items-end" data-id="element-1029">
            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(task.status)}`} data-id="element-1030">
              {getStatusLabel(task.status)}
            </span>
            <div className="flex gap-1.5" data-id="element-1031">
              {task.category && <span className="px-2.5 py-0.5 text-xs font-medium rounded-full border bg-blue-50 text-blue-700 border-blue-200" data-id="element-1032">
                  {task.category === 'Andet' ? 'Alm. Installation' : task.category}
                </span>}
              {task.taskType && <span className="px-2.5 py-0.5 text-xs font-medium rounded-full border bg-teal-50 text-teal-700 border-teal-200" data-id="element-1033">
                  {task.taskType}
                </span>}
            </div>
          </div>
        </div>
        {task.description && <p className="text-sm text-stone-600 mb-4 line-clamp-2" data-id="element-1034">
            {task.description}
          </p>}
        <div className="space-y-2" data-id="element-1035">
          {task.agreedPlanning && <div className="flex items-center gap-2 text-sm" data-id="element-1036">
              <WrenchIcon className="w-4 h-4 text-teal-600 flex-shrink-0" data-id="element-1037" />
              <span className="text-stone-500" data-id="element-1038">Aftalt projektering:</span>
              <span className="text-stone-900 font-medium" data-id="element-1039">
                {new Date(task.agreedPlanning).toLocaleDateString('da-DK')}
              </span>
            </div>}
          {task.agreedInstallation && <div className="flex items-center gap-2 text-sm" data-id="element-1040">
              <CalendarIcon className="w-4 h-4 text-teal-600 flex-shrink-0" data-id="element-1041" />
              <span className="text-stone-500" data-id="element-1042">Aftalt installation:</span>
              <span className="text-stone-900 font-medium" data-id="element-1043">
                {new Date(task.agreedInstallation).toLocaleDateString('da-DK')}
              </span>
            </div>}
          {task.hours !== undefined && <div className="flex items-center gap-2 text-sm" data-id="element-1044">
              <ClockIcon className="w-4 h-4 text-teal-600 flex-shrink-0" data-id="element-1045" />
              <span className="text-stone-500" data-id="element-1046">Timer:</span>
              <span className="text-stone-900 font-medium" data-id="element-1047">{task.hours}t</span>
            </div>}
        </div>
        {(task.quotePrice !== undefined || task.totalPrice !== undefined) && <div className="mt-4 pt-4 border-t border-stone-100" data-id="element-1048">
            <div className="flex items-center justify-between" data-id="element-1049">
              <span className="text-sm text-stone-500" data-id="element-1050">
                {task.taskType === 'Tilbudsopgave' ? 'Tilbudspris' : 'Samlet pris'}
              </span>
              <span className="text-lg font-semibold text-stone-900" data-id="element-1051">
                {task.taskType === 'Tilbudsopgave' ? (task.quotePrice || 0).toLocaleString('da-DK') : (task.totalPrice || 0).toLocaleString('da-DK')}{' '}
                kr
              </span>
            </div>
          </div>}
      </div>
    </motion.article>;
}